import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import Lightbox from "../components/Lightbox";
import WistiaPlayer from "../components/WistiaPlayer";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[44ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-galactico/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-galactico"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-85 max-w-[22ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

// Galleria sezione 6 — 6 slot, mapping 1:1 stretto.
// Slot oltre la lunghezza dell'array mostrano placeholder "Foto in arrivo".
const GALLERY_SLOTS = 4;
const GALLERY_PHOTOS = [
  // 01 — Tifose Hellas Verona davanti all'Arena (maglia home + away)
  "/images/assets/e7iivete_20240308_12-58-02_z81_8211-980x1470.jpg",
  // 02 — The Dugout: panchina-fermata davanti all'Arena con tifosi seduti
  "/images/assets/c55nwuk0_20240317_20240317_10-56-23_z62_9823.jpg",
  // 03 — Van ufficiale Hellas × Noleggiare in piazza Bra all'attivazione
  "/images/assets/8psjugl8_20240317_20240317_11-16-15_z62_0088.jpg",
  // 04 — Staff Noleggiare (felpa fucsia) all'attivazione davanti all'Arena
  "/images/assets/hubuef9d_20240317_20240317_10-47-13_z62_9727.jpg",
];
const LIGHTBOX_IMAGES = GALLERY_PHOTOS.map((src, i) => ({
  src,
  alt: `Noleggiare × Hellas Verona — The Dugout — Scatto ${String(i + 1).padStart(2, "0")} di ${GALLERY_SLOTS}`,
}));

const NoleggiareHellas = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const idx = WORKS.findIndex((w) => w.to === "/works/noleggiare-hellas");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = WORKS[(idx + 1) % WORKS.length];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Noleggiare × Hellas Verona", url: "/works/noleggiare-hellas" },
  ]);

  return (
    <>
      <Seo
        title="Noleggiare × Hellas: format The Dugout per Infront | OnSports Case"
        description="Format editoriale Noleggiare × Hellas Verona per Infront. The Dugout: dietro le quinte del calcio italiano in Serie A. OnSports Content & IP Studio."
        path="/works/noleggiare-hellas"
        image="/images/assets/e7iivete_20240308_12-58-02_z81_8211-980x1470.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/noleggiare-hellas/hero.jpg"
        imageAlt="The Dugout — Noleggiare × Hellas Verona × Infront"
        eyebrow="● Case study · Cultural Impact · Brand Experience · Content"
        titleLines={["Una panchina. Un viaggio.", "Un'ESPERIENZA."]}
        highlightWord="ESPERIENZA."
        subtitle="Abbiamo trasformato la sponsorship di Noleggiare per l'Hellas Verona in un'attivazione phygital che ha portato un gruppo di tifosi dal centro di Verona allo stadio Bentegodi a bordo dei van della squadra — passando per uno dei matchday più sentiti del campionato, Hellas-Milan."
        meta="2024  ·  Clienti — Noleggiare · Infront  ·  Categoria — Brand Experience & Content"
        overlayOpacity={0.55}
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    Noleggiare è uno dei principali brand italiani di autonoleggio.
                    Hellas Verona è il club di Serie A che racconta una città intera.
                    Infront è il gestore commerciale della partnership tra i due —
                    l'agenzia globale che cura la valorizzazione degli asset sponsor per
                    il club gialloblù. Quando i tre ci hanno coinvolti, l'obiettivo era
                    chiaro: tirare fuori dalla sponsorship qualcosa di più del logo a
                    bordo campo.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Volevano un'attivazione che mettesse al centro i tifosi, sfruttasse
                    gli asset on-site dell'Hellas e raccontasse Noleggiare nella sua
                    natura più autentica — il viaggio. Da questa convergenza è nato{" "}
                    <strong className="font-semibold">The Dugout</strong>: una panchina
                    da calcio installata nel centro di Verona, trasformata in fermata
                    d'autobus brandizzata, da cui un gruppo selezionato di tifosi è
                    partito a bordo dei van ufficiali della squadra per vivere il
                    matchday Hellas-Milan all'interno dello stadio Bentegodi, ospiti del
                    Pitch Box di Noleggiare.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Progetto">The Dugout</SchedaRow>
                    <SchedaRow label="Clienti">
                      Noleggiare · Hellas Verona · Infront
                    </SchedaRow>
                    <SchedaRow label="Ruolo">
                      Ideazione creativa · Gestione organizzativa<br />
                      Produzione video e foto · Social seeding
                    </SchedaRow>
                    <SchedaRow label="Matchday">
                      Hellas Verona — Milan, Serie A 2023/24
                    </SchedaRow>
                    <SchedaRow label="Location">
                      Centro di Verona + Stadio Bentegodi
                    </SchedaRow>
                    <SchedaRow label="Pilastro">
                      Cultural Impact · Brand Experience
                    </SchedaRow>
                    <SchedaRow label="Anno">2024</SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Far uscire una sponsorship dal bordo campo.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Per un brand consumer come Noleggiare, le sponsorship sportive
                  tradizionali — logo sulle maglie, banner LED a bordo campo, mention
                  durante la partita — generano awareness ma non costruiscono
                  memorabilità. Sono visibilità misurabile in numero di esposizioni, ma
                  raramente in storie che le persone raccontano agli altri.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  La sfida era prendere gli asset di una partnership già esistente — gli
                  accessi al Bentegodi, i van ufficiali della squadra, il Pitch Box, il
                  diritto di marca su Hellas — e usarli come ingredienti di
                  un'esperienza vera, capace di trasformare un gruppo di tifosi in
                  protagonisti per un giorno. Una storia con un'idea visiva forte alla
                  base: una panchina che diventa fermata d'autobus.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL FORMAT / VIDEO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-dugout">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>The Dugout</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  La panchina che è diventata una fermata.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Abbiamo installato una panchina da calcio brandizzata Noleggiare ×
                  Hellas nel pieno centro di Verona, a pochi passi dall'Arena. Da quel
                  punto — visivamente coerente con la tribuna dello stadio e leggibile a
                  colpo d'occhio — è partito il viaggio dei tifosi verso il Bentegodi, a
                  bordo dei van ufficiali della squadra. Due video raccontano
                  l'esperienza: il teaser di lancio e il reportage dal matchday.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <RevealOnScroll delay={0.05}>
              <article className="flex flex-col" data-testid="video-teaser">
                <span className="font-condensed uppercase tracking-widest text-[14px] text-lime mb-3">
                  Teaser di lancio
                </span>
                <WistiaPlayer mediaId="mkvzx6jogl" aspect={1.7777777777777777} />
                <p className="mt-4 text-[14px] leading-[1.55] opacity-80">
                  Il video che ha lanciato l'attivazione sui canali organici di Hellas
                  e Noleggiare.
                </p>
              </article>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <article className="flex flex-col" data-testid="video-matchday">
                <span className="font-condensed uppercase tracking-widest text-[14px] text-lime mb-3">
                  Matchday · Il reportage
                </span>
                <WistiaPlayer mediaId="5zmjjiwrq6" aspect={1.7777777777777777} />
                <p className="mt-4 text-[14px] leading-[1.55] opacity-80">
                  Dal Walk About allo stadio fino al fischio d'inizio: l'esperienza dei
                  tifosi al Bentegodi.
                </p>
              </article>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Dall'idea creativa al matchday.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Abbiamo gestito il progetto end-to-end, dall'intuizione creativa
                  iniziale alla post-produzione dei contenuti — passando per tutta la
                  logistica del giorno della partita. Un'unica regia per garantire
                  coerenza tra concept, esecuzione e narrazione.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Ideazione creativa"
                body="Concept della panchina-fermata e di tutta l'identità visiva dell'attivazione. Definizione del format esperienziale (Walk About, accesso al Pitch Box) coerente con il posizionamento di Noleggiare sul viaggio e con i codici del tifo gialloblù."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Gestione organizzativa"
                body="Coordinamento operativo end-to-end: installazione fisica della panchina nel centro di Verona, selezione dei tifosi partecipanti, logistica dei van, gestione dei permessi, scaletta dell'esperienza al Bentegodi in collaborazione con Hellas Verona e Infront."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="Contenuti video e foto"
                body="Produzione integrale del teaser di lancio e del reportage del matchday. Reportage fotografico dell'esperienza dei tifosi — dal Walk About all'accoglienza nel Pitch Box di Noleggiare. Versioning multi-formato per i diversi canali coinvolti."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.26}>
              <ApproccioBlock
                num="04"
                title="Social seeding"
                body="Distribuzione coordinata sui canali organici di Hellas Verona e Noleggiare. Format ADV dedicati per campagne paid su Meta. Calendario di pubblicazione costruito attorno alla settimana del matchday per massimizzare la curva di attenzione."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 6 — GALLERIA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-gallery">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Visual</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Verona, 8 marzo 2024.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch] opacity-90">
                  Una selezione di scatti dal giorno dell'attivazione: la panchina nel
                  centro di Verona, il viaggio sui van, il Walk About al Bentegodi, il
                  matchday Hellas-Milan dal Pitch Box di Noleggiare.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 grid-flow-row-dense gap-2"
            data-testid="gallery-grid"
          >
            {Array.from({ length: GALLERY_SLOTS }).map((_, i) => {
              const span2 = i === 0 || i === 3; // 1° e 4° slot: 2 colonne su desktop
              const wrapperClass = span2 ? "md:col-span-2" : "";
              const photo = GALLERY_PHOTOS[i];

              if (!photo) {
                return (
                  <RevealOnScroll
                    key={i}
                    delay={Math.min(i, 6) * 0.04}
                    className={wrapperClass}
                  >
                    <div
                      className="block w-full relative bg-dodgers/[0.05] border border-lime flex items-center justify-center"
                      style={{ aspectRatio: "4 / 3" }}
                      data-testid={`gallery-placeholder-${i}`}
                      aria-label={`Scatto ${i + 1} di ${GALLERY_SLOTS} — in arrivo`}
                    >
                      <span
                        className="font-condensed uppercase tracking-widest text-galactico"
                        style={{ fontSize: "12px" }}
                      >
                        Foto in arrivo
                      </span>
                    </div>
                  </RevealOnScroll>
                );
              }

              return (
                <RevealOnScroll
                  key={i}
                  delay={Math.min(i, 6) * 0.04}
                  className={wrapperClass}
                >
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className="block w-full overflow-hidden relative group focus:outline-none"
                    style={{ aspectRatio: "4 / 3" }}
                    data-testid={`gallery-${i}`}
                    data-hover="true"
                    aria-label={`Apri scatto ${i + 1} di ${GALLERY_SLOTS}`}
                  >
                    <img
                      src={photo}
                      alt={LIGHTBOX_IMAGES[i].alt}
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                      draggable="false"
                    />
                    <span
                      className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  </button>
                </RevealOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      <Lightbox
        images={LIGHTBOX_IMAGES}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      {/* SEZ. 7 — IMPATTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Cosa è rimasto, dopo il fischio finale.
                </h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="1" label="Panchina-fermata installata nel centro di Verona" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="3" label="Partner integrati — Noleggiare · Hellas · Infront" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="Serie A" label="Matchday Hellas · Milan al Bentegodi" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock
                value="Phygital"
                label="Activation end-to-end dall'ideazione al post-match"
              />
            </RevealOnScroll>
          </div>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Memorabilità
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Un gruppo di tifosi ha vissuto un matchday che non dimenticheranno. È
                  la differenza tra un'esposizione di brand pagata in CPM e una storia
                  che le persone raccontano agli amici la settimana dopo. È quello che
                  le sponsorship dovrebbero generare e che raramente generano.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Tre partner allineati
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Noleggiare ha portato il prodotto (il viaggio, i van), Hellas Verona
                  ha portato l'accesso (Bentegodi, Pitch Box, identità del club),
                  Infront ha tenuto insieme la governance commerciale della
                  partnership. OnSports ha costruito il format che ha fatto convergere
                  i tre obiettivi su un'unica narrazione.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Contenuto come asset duraturo
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  I video di lancio e di reportage hanno avuto vita oltre il matchday.
                  Sono diventati case material per Noleggiare e Hellas, dimostrazione
                  tangibile di cosa significhi attivare una partnership invece che
                  limitarsi a esporla.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 8 — CREDITS */}
      <section className="py-20 md:py-28 bg-galactico text-dodgers" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Clienti">Noleggiare · Hellas Verona · Infront</SchedaRow>
                <SchedaRow label="Progetto">The Dugout</SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Ideazione creativa · Gestione organizzativa<br />
                  Produzione video e foto · Social seeding
                </SchedaRow>
                <SchedaRow label="Servizi">
                  Sponsorship &amp; Impact Programs<br />
                  Content &amp; IP Studio
                </SchedaRow>
                <SchedaRow label="Matchday">Hellas Verona — Milan, marzo 2024</SchedaRow>
                <SchedaRow label="Location">
                  Centro di Verona · Stadio Bentegodi
                </SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[28ch]">
              Hai una sponsorship che vorresti fosse qualcosa di più di un logo?
              Costruiamo insieme il suo <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Lavoriamo con brand, club e agenzie di gestione commerciale che vogliono
              uscire dalla logica della visibilità per costruire attivazioni che
              generano memorabilità reale. Raccontaci la tua sponsorship.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default NoleggiareHellas;
